<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-stepper :steps="rmSteps" :current="currentStep" />
        <hm-spinner v-if="!ready" />
        <Loading :active="busy" :is-full-page="false">
          <slot>
            <hm-spinner />
          </slot>
        </Loading>
        <hm-alert />

        <b-form @submit.prevent="onSubmit" v-show="ready">
          <h4>Step 5: Create A Risk Management Plan</h4>
          <hr />
          <p>The purpose of a risk management plan is to provide structure for evaluation, prioritization, and implementation of risk-reducing security measures. For your risk management plan to be successful, be sure to involve key decision makers in this process.</p>
          <p>Create a plan to manage risk for each threat-vulnerability combination, starting with the highest risk level.</p>
          <b-form-group>
            <b-table class="p-1" responsive bordered hover show-empty empty-text="No Threat Vulnerability" :items="threats" :fields="columns" @row-clicked="rowClick">
              <template slot="cell(threat)" slot-scope="data">
                {{data.item.threat}}
                <hm-popover v-if="data.item.threatInfo" :title="data.item.threat" :content="data.item.threatInfo" />
              </template>
              <template slot="cell(vuln)" slot-scope="data">
                {{data.item.vuln}}
                <hm-popover v-if="data.item.vulnInfo" :title="data.item.vuln" :content="data.item.vulnInfo" />
              </template>
              <template slot="cell(risk)" slot-scope="data">
                <strong>{{data.item.risk}}</strong>
              </template>
              <template slot="cell(plan)" slot-scope="data">
                <span v-if="data.item.plan">
                  <i class="icon-check text-success"></i>
                </span>
                <b-badge v-else variant="danger">START</b-badge>
              </template>
            </b-table>
          </b-form-group>

          <hr />
          <b-row>
            <b-col cols="6">
              <b-button type="button" variant="secondary" class="px-4" :disabled="busy" @click="onBack">Back</b-button>
            </b-col>
            <b-col cols="6" class="text-right">
              <b-button type="submit" variant="primary" class="px-4" :disabled="busy || !planComplete">Next</b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { riskService } from '@/services'
import Enumerable from 'linq/linq.js'

export default {
  name: 'RiskPlanList',
  data() {
    return {
      rm: {},
      threats: [],
      columns: [{
        key: 'risk',
        label: 'Risk',
        tdClass: (value, key, item) => {
          return ['text-center', 'risk-' + value];
        }
      }, {
        key: 'threat',
        label: 'Threat'
      }, {
        key: 'vuln',
        label: 'Vulnerability'
      }, {
        key: 'plan',
        label: 'Plan',
        tdClass: (value, key, item) => {
          return ['text-center'];
        }
      }],
      ready: false,
      busy: false,
      submitted: false,
      currentStep: 5
    }
  },
  computed: {
    ...mapState(['rmSteps']),
    ...mapState('maps', ['threatMap']),
    planComplete() {
      return (Enumerable.from(this.threats).where(x => { return !x.plan; }).count() == 0);
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertException: 'alert/exception'
    }),
    rowClick(record, index) {
      this.busy = true;
      this.alertClear();

      this.$router.push({ name: 'RiskPlan', params: { id: record.id } });
      this.busy = false;
    },
    onBack() {
      this.busy = true;
      this.alertClear();

      // reset items when backing up
      this.rm.approved = false;
      this.rm.step = this.currentStep - 2;

      // flag safeguards incomplete
      for (var key in this.rm.safeguards) {
        if (this.rm.safeguards.hasOwnProperty(key)) {
          this.rm.safeguards[key].approved = false;
          this.rm.safeguards[key].implement = false;
          this.rm.safeguards[key].list = null;
        }
      }

      // flag threat plan incomplete
      for (let i = 0; i < this.rm.threats.length; i++) {
        this.rm.threats[i].plan = false;
      }

      riskService.setRiskManagement(this.rm)
        .then(data => {
          this.$router.push('/risk/report');
        })
        .catch(err => {
          this.alertException({ err, message: 'Error saving risk management.' });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.rm.step = this.currentStep;

      riskService.setRiskManagement(this.rm)
        .then(data => {
          this.$router.push('/risk/approve');
        })
        .catch(err => {
          this.alertException({ err, message: 'Error saving risk management.' });
        })
        .finally(() => {
          this.busy = false;
        });
    }
  },
  created() {
    this.ready = false;

    Promise.all([
      this.$store.dispatch('maps/fetchThreatMap'),
      riskService.getCurrentRiskManagement()
    ]).then((values) => {
      this.rm = values[1];

      this.threats = Enumerable.from(this.threatMap)
        .join(this.rm.threats,
          function (o) { return o.id + ':' + o.revision; },
          function (i) { return i.id + ':' + i.revision; },
          function (o, i) { return { id: o.id, revision: o.revision, threat: o.threat, threatInfo: o.threatInfo, vuln: o.vuln, vulnInfo: o.vulnInfo, active: i.active, approved: i.approved, plan: i.plan, risk: i.chance * i.impact }; })
        .where(function (x) { return x.active; })
        .orderByDescending((x) => { return x.risk; })
        .toArray();
    }).catch((err) => {
      this.alertException({ err, message: 'Error loading risk management data.' });
    }).finally(() => {
      this.ready = true;
    });
  }
}
</script>

<style lang="scss">
// import variables for this application
@import '@/assets/scss/_variables.scss';

/* risk colors */
.risk-1 {
  color: white;
  background-color: #00ff33;
}
.risk-2 {
  color: white;
  background-color: #00ff33;
}
.risk-3 {
  color: white;
  background-color: #33cc00;
}
.risk-4 {
  color: white;
  background-color: #33cc00;
}
.risk-5 {
  color: white;
  background-color: #33cc00;
}
.risk-6 {
  color: white;
  background-color: #99cc33;
}
.risk-8 {
  color: white;
  background-color: #ffcc00;
}
.risk-9 {
  color: white;
  background-color: #ffcc00;
}
.risk-10 {
  color: white;
  background-color: #ff9900;
}
.risk-12 {
  color: white;
  background-color: #ff6633;
}
.risk-15 {
  color: white;
  background-color: #cc0000;
}
.risk-16 {
  color: white;
  background-color: #990000;
}
.risk-20 {
  color: white;
  background-color: #330000;
}
.risk-25 {
  color: white;
  background-color: #000000;
}
</style>